import { useEffect } from 'react';
import { subscriptionsApi } from '@/lib/auth_api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * 2026-10 — Handles the Stripe subscription-checkout return flow.
 *
 * success_url pattern (from subscriptions_service.py):
 *   {origin}/?subscription=success&billing_mode={standard|byok}&session_id=…
 *
 * The Stripe webhook applies the upgrade asynchronously, so we poll
 * `/subscriptions/me` waiting for the tier AND billing_mode to land
 * before surfacing the "Welcome" toast and calling onUpgraded().
 *
 * BYOK welcome flag
 * ─────────────────
 * When a BYOK checkout completes we write `byok_welcome_pending=1` to
 * localStorage.  BillingPanel reads that flag on mount and shows a
 * one-time "paste your key" banner, then clears it.
 */
export function useSubscriptionReturn(onUpgraded) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const flag          = searchParams.get('subscription');
    const billingMode   = searchParams.get('billing_mode') || 'standard'; // new param in success_url

    if (!flag) return;

    if (flag === 'cancelled') {
      toast.info('Upgrade cancelled.');
      navigate('/', { replace: true });
      return;
    }

    if (flag === 'success') {
      let cancelled = false;

      const poll = async (attempt = 0) => {
        if (cancelled || attempt > 12) return;
        try {
          const me = await subscriptionsApi.me();

          // For a billing-mode switch the tier was already set before
          // checkout, so `me.tier && me.plan` would be true immediately —
          // well before the webhook fires and `billing_mode` is persisted.
          // We therefore wait until me.billing_mode matches the expected
          // mode (or fall back to 'standard' after 12 attempts ≈ 24 s).
          const tierLanded = me.tier && me.plan;
          const modeLanded = !billingMode || billingMode === 'standard'
            ? (me.billing_mode || 'standard') === 'standard' || tierLanded
            : me.billing_mode === billingMode;

          if (tierLanded && modeLanded) {
            const planLabel = me.plan?.name ?? me.tier;
            if (billingMode === 'byok') {
              toast.success(`BYOK mode active on ${planLabel}!`, {
                description: 'Go to Settings → Billing to paste your API key.',
                duration: 6000,
              });
              // Signal BillingPanel to show the one-time API-key nudge.
              localStorage.setItem('byok_welcome_pending', '1');
            } else {
              toast.success(`Welcome to ${planLabel}!`);
            }
            onUpgraded?.();
            navigate('/app', { replace: true });
            return;
          }
        } catch (err) {
          if (attempt === 0) console.warn('[subscriptions] status check failed', err);
        }
        setTimeout(() => poll(attempt + 1), 2000);
      };

      poll();
      return () => { cancelled = true; };
    }
  }, [searchParams, navigate, onUpgraded]);
}
